import { AsyncPropSelector, getTranslatedText } from '@nile/components';
import {
  DropdownIndicator,
  IndicatorSeparator,
  formatGroupLabel,
  formatOptionLabel
} from '@nile/components/src/Pebbles/select';
import { useQuery, GET_RULEBUILDER_OPERATORS } from '@nile/graphql';
import DateFilter from '@nile/reports/src/modules/reports/utils/DateFilter';

import React, { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { injectIntl } from 'react-intl';
import { cloneDeep, includes } from 'lodash';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { Button, Checkbox, TextField } from '@superops/ui-design-system';
import styled from 'styled-components';

/**
 * This method overrides filterType if there is a meta that wants the normal behavior to be overridden.
 * @param {Object} property
 * @param {Array} meta
 * @returns {Object}
 */
const appendProperties = (property, meta) => {
  const updatedProperty = { ...property };
  const propertyMeta = meta?.find((el) => el.name === property.name);
  const hasMetaToModify = propertyMeta?.filterType;
  if (hasMetaToModify) {
    updatedProperty.filterType = propertyMeta.filterType;
  }
  return updatedProperty;
};

function InlineFilter(props) {
  const flags = useFlags();
  const { intl, meta, allConditions } = props;
  const {
    personalization,
    updateCondition,
    currentViewConditions,
    allowedProps,
    entity,
    getDefaultValue,
    getDynamicQuery,
    checkDynamicQuery,
    generateOptions,
    getMethodName,
    getKeyName,
    getAsyncOptions,
    fetchValues,
    getVariables,
    getDateOperators,
    updateConditions,
    checkCustomAttribute,
    field: incomingField,
    getAttributeName,
    getEntityAttributeName,
    filterMeta = [],
    getConditionAttributes = null
  } = meta;
  const field = appendProperties(incomingField, filterMeta);

  const [condition, setCondition] = useState({
    joinOperator: 'AND',
    operands: []
  });
  const [filterExpression, setFilterExpression] = useState('');
  const [autoCompleteMenuOpen, setAutoCompleteMenuOpen] = useState(false);

  const {
    loading: operatorsLoading,
    error: operatorsError,
    data: allOperators
  } = useQuery(GET_RULEBUILDER_OPERATORS);

  useEffect(() => {
    const allOperands = cloneDeep(allConditions?.operands || []);
    const ruleIndex = allOperands.findIndex((el) => {
      if (el.attribute?.indexOf('.') > -1) {
        const attr = el.attribute.split('.')[0];
        if (checkCustomAttribute) {
          return checkCustomAttribute(el.attribute, field);
        }
        return attr === field.name || attr === field.name.split('.')[0];
      }
      return el.attribute === getRuleAttribute(field);
    });
    setCondition({
      ...condition,
      operands:
        ruleIndex > -1
          ? [allOperands[ruleIndex]]
          : allConditions
          ? condition.operands
          : []
    });
  }, [JSON.stringify(allConditions)]);

  const getValue = (f, val) => {
    if (f.dataType === 'daterange' && typeof val === 'object') return val;
    switch (f.dataType) {
      case 'date':
      case 'datetime':
        return val.key;
      case 'boolean':
      case 'daterange':
        return val;
      default:
        break;
    }
    switch (f.filterType) {
      case 'text':
      case 'numeric':
      case 'decimal':
      case 'paragraph':
      case 'read_only':
        return val || null;
      default:
        return val ? val.map((el) => el.value) : [];
    }
  };

  const getOperator = (f, val) => {
    if (f.dataType === 'daterange' && typeof val === 'object') return 'between';
    switch (f.dataType) {
      case 'date':
      case 'datetime':
      case 'daterange':
        return 'on';
      case 'boolean':
        return 'is';
      default:
        break;
    }
    switch (f.filterType) {
      case 'numeric':
      case 'decimal':
        return 'is';
      case 'text':
      case 'paragraph':
      case 'read_only':
        return 'contains';
      default:
        return 'includes';
    }
  };

  const getRuleAttribute = (f) => {
    if (getAttributeName) {
      return getAttributeName(f);
    }

    if (f.name === 'prioritizedId') {
      return 'displayId';
    }
    if (f.name === 'patchDetail.title') {
      return f.name;
    }
    if (f?.name?.includes('patchDetail.')) {
      return f?.name?.split('.')[1];
    }
    if (f.name === 'invoiceId') {
      return 'displayId';
    }
    return f.name;
  };

  const getRuleName = () => {
    if (incomingField.name === 'patchDetail.kbNumbers.kbNumber') {
      return 'patchDetail.kbNumbers.kbNumber';
    }
    if (
      incomingField.name === 'kbNumbers' ||
      incomingField.name === 'patchDetail.kbNumbers'
    ) {
      return 'kbNumbers.kbNumber';
    }
    const currentField = incomingField;
    if (currentField?.dataType === 'entity') {
      return getEntityAttributeName
        ? getEntityAttributeName(currentField)
        : `${currentField.name}.name`;
    }
    return getRuleAttribute(currentField) || currentField?.name;
  };

  function getPropertyAttribute(property, data = '') {
    if (
      property.name === 'kbNumbers' ||
      property.name === 'patchDetail.kbNumbers'
    ) {
      return 'kbNumbers.kbNumber';
    }
    if (property.name === 'patchDetail.kbNumbers.kbNumber') {
      return 'patchDetail.kbNumbers.kbNumber';
    }
    if (getConditionAttributes && getConditionAttributes(property)) {
      return getConditionAttributes(property);
    }
    if (data) {
      return property.dataType === 'entity'
        ? getEntityAttributeName
          ? data.attribute === getEntityAttributeName(property)
          : data.attribute === `${property.name}.name`
        : data.attribute === getRuleAttribute(property);
    }
    return property.dataType === 'entity'
      ? getEntityAttributeName
        ? getEntityAttributeName(property)
        : `${property.name}.name`
      : getRuleAttribute(property);
  }
  const getCondition = (f, val) => {
    const operands = cloneDeep(condition.operands);
    const ruleIdx = operands.findIndex((el) => {
      if (f.name === 'patchDetail.kbNumbers.kbNumber') {
        return el.attribute === 'patchDetail.kbNumbers.kbNumber';
      }
      // For all other cases, use the existing attribute matching logic
      return getPropertyAttribute(f, el);
    });
    if (ruleIdx > -1) {
      if (val?.hasOwnProperty('key')) {
        operands[ruleIdx] = {
          ...operands[ruleIdx],
          operator: getOperator(f, val),
          value: getValue(f, val)
        };
      } else if (val?.length) {
        operands[ruleIdx] = {
          ...operands[ruleIdx],
          operator: getOperator(f, val),
          value: getValue(f, val)
        };
      } else {
        operands.splice(ruleIdx, 1);
      }
    } else {
      operands.push({
        attribute: getPropertyAttribute(f),
        operator: getOperator(f, val),
        value: getValue(f, val)
      });
    }
    return operands;
  };

  const updateSelection = (f, val) => {
    const operands = getCondition(f, val);
    setCondition({ ...condition, operands: operands });
  };

  const updateFilterConditions = (externalCondition) => {
    const ruleAttribute = getRuleName(field);
    // Whenever the filter is applied , we'll update the attribute based on helpers.
    // For example if the field is entity, we'll the name will be technician but the attribute name will be technician.name
    // This is handled by the helper functions passed.
    // Now this ruleAttribute will be passed to updateConditions as 3rd param and will be accessed in SuperListHook updateTableFilter method
    updateConditions(externalCondition || condition, field, ruleAttribute);
  };

  const searchOnEnter = (flagToCheck, e) => {
    if (flags?.[flagToCheck]) {
      if (e.key === 'Enter') {
        updateFilterConditions();
      }
    }
  };

  const getComponent = (property) => {
    const defaultValue =
      getDefaultValue &&
      getDefaultValue(property, condition, getConditionAttributes);
    const isDynamicQuery = checkDynamicQuery(property);
    const dynamicQuery = isDynamicQuery
      ? getDynamicQuery.bind(this, property)
      : null;
    if (filterExpression) {
      if (
        property.label &&
        property.label.toLowerCase().indexOf(filterExpression) === -1
      )
        return '';
    }
    if (property.autoComplete) {
      const loadOptions = (inputValue, callback) => {
        if (inputValue.length < 2) return callback([]);
        const asyncOptions = getAsyncOptions(property);
        fetchValues(asyncOptions.client, asyncOptions.query, {
          inputValue,

          property
        }).then((res) => {
          return callback(res);
        });
      };
      return (
        <SearchField>
          <DropdownWrap>
            <AsyncSelect
              isMulti
              cacheOptions
              classNamePrefix="value_selector"
              loadOptions={loadOptions}
              value={defaultValue}
              onChange={updateSelection.bind(this, property)}
              onMenuOpen={() => {
                setAutoCompleteMenuOpen(true);
              }}
              onMenuClose={() => {
                setAutoCompleteMenuOpen(false);
              }}
              components={{ IndicatorSeparator, DropdownIndicator }}
              // closeMenuOnScroll={(e) => {
              //   return (
              //     e.target &&
              //     e.target.classList &&
              //     !(e.target.classList.value.indexOf('menu-list') > -1)
              //   );
              // }}
              name="valueselector"
              menuPortalTarget={document.body}
              menuPlacement="auto"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 })
              }}
              noOptionsMessage={(params) => {
                return params?.inputValue?.length < 2
                  ? 'Type 2 or more characters to search'
                  : 'No options';
              }}
              onKeyDown={
                !autoCompleteMenuOpen
                  ? searchOnEnter.bind(this, 'multiselectSearchOnEnter')
                  : undefined
              }
            />
          </DropdownWrap>
        </SearchField>
      );
    }
    if (property.isCustom) {
      if (
        property.dataType === 'string' &&
        includes(['text', 'paragraph'], property.filterType)
      ) {
        return (
          <SearchField
            onKeyDown={searchOnEnter.bind(this, 'textFieldSearchOnEnter')}
          >
            <TextField
              placeholder={getTranslatedText(
                intl,
                'ticket.new.columns.contained.pl'
              )}
              value={defaultValue}
              onChange={updateSelection.bind(this, property)}
            />
          </SearchField>
        );
      }
      if (property.dataType === 'number') {
        return (
          <SearchField
            onKeyDown={searchOnEnter.bind(this, 'textFieldSearchOnEnter')}
          >
            <TextField
              placeholder={getTranslatedText(
                intl,
                'ticket.new.filter.entername'
              )}
              value={defaultValue}
              onChange={updateSelection.bind(this, property)}
              type="number"
            />
          </SearchField>
        );
      }
      if (includes(['date', 'datetime'], property.dataType)) {
        // const options = getDateOperators(intl);
        const options =
          allOperators?.getRuleBuilderOperators?.operator_placeholders?.on?.map(
            (el) => {
              return {
                ...el,
                label: getTranslatedText(intl, el.key),
                value: getTranslatedText(intl, el.key),
                name: el.key
              };
            }
          ) || getDateOperators(intl);
        return (
          <SearchField>
            <Select
              className="propSelector"
              key={defaultValue}
              classNamePrefix="value_selector"
              formatGroupLabel={formatGroupLabel}
              formatOptionLabel={formatOptionLabel}
              value={options.find((el) => el.key === defaultValue)}
              components={{ IndicatorSeparator, DropdownIndicator }}
              onChange={(val) => {
                updateSelection(property, val);
                if (flags?.dateSearchOnEnter) {
                  const operands = getCondition(property, val);
                  updateFilterConditions({ ...condition, operands: operands });
                }
              }}
              options={options}
              placeholder={getTranslatedText(
                intl,
                'admin.slaPolicy.chooseValue'
              )}
              menuPortalTarget={document.body}
              menuPlacement="auto"
              isClearable
              // closeMenuOnScroll={(e) => {
              //   e.stopPropagation();
              //   e.preventDefault();
              //   return (
              //     e.target &&
              //     e.target.classList &&
              //     !(e.target.classList.value.indexOf('menu-list') > -1)
              //   );
              // }}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </SearchField>
        );
      }
      return (
        <SearchField>
          <AsyncPropSelector
            autoFocus
            hideTitle
            defaultValue={defaultValue}
            updateSelection={updateSelection}
            field={property}
            isDynamicQuery={isDynamicQuery}
            dynamicQuery={dynamicQuery}
            generateOptions={generateOptions}
            isMulti
            entity={entity || 'Ticket'}
            variables={getVariables(property)}
            onKeyDown={searchOnEnter.bind(this, 'multiselectSearchOnEnter')}
          />
        </SearchField>
      );
    }
    if (
      property.dataType === 'string' &&
      !property.isCustom &&
      includes(['text', 'read_only'], property.filterType)
    ) {
      return (
        <SearchField
          onKeyDown={searchOnEnter.bind(this, 'textFieldSearchOnEnter')}
        >
          <TextField
            placeholder={getTranslatedText(intl, 'ticket.new.filter.entername')}
            value={defaultValue}
            onChange={updateSelection.bind(this, property)}
          />
        </SearchField>
      );
    }
    if (property.dataType === 'string' && !property.isCustom) {
      return (
        <SearchField>
          <AsyncPropSelector
            autoFocus
            hideTitle
            defaultValue={defaultValue}
            updateSelection={updateSelection}
            field={property}
            isDynamicQuery={isDynamicQuery}
            dynamicQuery={dynamicQuery}
            generateOptions={generateOptions}
            isMulti
            entity={entity || 'Ticket'}
            onKeyDown={searchOnEnter.bind(this, 'multiselectSearchOnEnter')}
          />
        </SearchField>
      );
    }
    if (property.dataType === 'boolean') {
      return (
        <SearchField>
          <Checkbox
            checked={defaultValue}
            onChange={updateSelection.bind(this, property)}
          />
        </SearchField>
      );
    }
    if (property.dataType === 'number') {
      return (
        <SearchField
          onKeyDown={searchOnEnter.bind(this, 'textFieldSearchOnEnter')}
        >
          <TextField
            placeholder={getTranslatedText(intl, 'ticket.new.filter.entername')}
            value={defaultValue}
            onChange={updateSelection.bind(this, property)}
            type="number"
          />
        </SearchField>
      );
    }
    if (property.dataType === 'entity') {
      if (['numeric'].includes(property.filterType)) {
        return (
          <SearchField
            onKeyDown={searchOnEnter.bind(this, 'textFieldSearchOnEnter')}
          >
            <TextField
              placeholder={getTranslatedText(
                intl,
                'ticket.new.filter.entername'
              )}
              value={defaultValue}
              onChange={updateSelection.bind(this, property)}
            />
          </SearchField>
        );
      }
      return (
        <SearchField>
          <AsyncPropSelector
            autoFocus
            hideTitle
            defaultValue={defaultValue}
            updateSelection={updateSelection}
            field={property}
            isDynamicQuery={isDynamicQuery}
            dynamicQuery={dynamicQuery}
            generateOptions={generateOptions}
            isMulti
            entity={entity || 'Ticket'}
            variables={getVariables(property)}
            onKeyDown={searchOnEnter.bind(this, 'multiselectSearchOnEnter')}
          />
        </SearchField>
      );
    }
    if (includes(['daterange'], property.dataType)) {
      return (
        <DateFilter
          setDate={(d) => {
            updateSelection(property, d);
            if (flags?.dateSearchOnEnter) {
              const operands = getCondition(property, d);
              updateFilterConditions({ ...condition, operands: operands });
            }
          }}
          date={defaultValue}
        />
      );
    }
    if (includes(['date', 'datetime'], property.dataType)) {
      const options = getDateOperators(intl);
      return (
        <SearchField>
          <Select
            className="propSelector"
            key={defaultValue}
            classNamePrefix="value_selector"
            formatGroupLabel={formatGroupLabel}
            formatOptionLabel={formatOptionLabel}
            value={options.find((el) => el.key === defaultValue)}
            components={{ IndicatorSeparator, DropdownIndicator }}
            onChange={(val) => {
              updateSelection(property, val);
              if (flags?.dateSearchOnEnter) {
                const operands = getCondition(property, val);
                updateFilterConditions({ ...condition, operands: operands });
              }
            }}
            options={options}
            placeholder={getTranslatedText(intl, 'admin.slaPolicy.chooseValue')}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            isClearable
          />
        </SearchField>
      );
    }
  };
  const ruleIndex = allConditions?.operands?.findIndex((el) => {
    if (el.attribute?.indexOf('.') > -1) {
      if (checkCustomAttribute) {
        return checkCustomAttribute(el.attribute, field);
      }
      const attr = el.attribute.split('.')[0];
      if (field.name.indexOf('.') > -1) {
        return attr === field.name.split('.')[0];
      }
      return attr === field.name;
    }
    return el.attribute === field.name;
  });
  if (operatorsLoading) return null;
  return (
    <div>
      {getComponent(field)}
      <ActionButton
        className="action-button"
        isVisible={!!condition?.operands?.length || ruleIndex > -1}
      >
        <Button
          plain
          onClick={() => {
            updateFilterConditions();
          }}
        >
          Search
        </Button>
      </ActionButton>
    </div>
  );
}

const DropdownWrap = styled.div`
  width: 98%;
  padding-left: 2px;
`;

const ActionButton = styled.div`
  display: flex;
  justify-content: flex-end;
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')} !important;
`;

const SearchField = styled.div`
  margin-bottom: 10px;
  .Polaris-TextField {
    margin-bottom: 0 !important;
    height: 38px;
  }
`;

export default injectIntl(InlineFilter);
